import { useState } from 'react';
import { useTheme } from '../../../hooks/useTheme';
import type { IndustrySector } from '../../../mocks/industries';

interface SectorSectionProps {
  sector: IndustrySector;
  reverse?: boolean;
}

export default function SectorSection({ sector, reverse = false }: SectorSectionProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const cardBg = isLight ? 'bg-white' : 'rgba(6,12,28,0.95)';
  const borderColor = isLight ? 'rgba(10,22,40,0.08)' : 'rgba(255,255,255,0.07)';
  const textColor = isLight ? '#0a1628' : '#ffffff';
  const subColor = isLight ? 'rgba(10,22,40,0.60)' : 'rgba(255,255,255,0.55)';

  return (
    <section
      id={sector.id}
      className="py-20 px-4 sm:px-6 scroll-mt-20"
      style={{
        background: isLight
          ? 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)'
          : 'linear-gradient(180deg, #0a1628 0%, #060c1c 100%)',
        borderTop: `1px solid ${borderColor}`,
      }}
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row items-start gap-10 mb-14">
          {/* Left: text */}
          <div className={`flex-1 ${reverse ? 'lg:order-2' : ''}`}>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-11 h-11 flex items-center justify-center rounded-2xl"
                style={{ backgroundColor: sector.accentColor + '18', color: sector.accentColor }}
              >
                <i className={`${sector.icon} text-xl`} />
              </div>
              <div
                className="h-px flex-1"
                style={{ background: `linear-gradient(to right, ${sector.accentColor}40, transparent)` }}
              />
            </div>

            <h2
              className="font-heading text-3xl sm:text-4xl font-extrabold mb-3 leading-snug"
              style={{ color: textColor }}
            >
              {sector.subtitle}
            </h2>
            <p
              className="font-body text-base leading-relaxed mb-5 max-w-xl"
              style={{ color: subColor }}
            >
              {sector.description}
            </p>
            <p
              className="font-body text-sm leading-relaxed mb-7 max-w-xl"
              style={{ color: isLight ? 'rgba(10,22,40,0.50)' : 'rgba(255,255,255,0.42)' }}
            >
              {sector.challenge}
            </p>

            {/* Stats row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-7">
              {sector.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="rounded-xl p-3 text-center"
                  style={{
                    backgroundColor: sector.accentColor + '0d',
                    border: `1px solid ${sector.accentColor}25`,
                  }}
                >
                  <div
                    className="font-heading text-lg font-extrabold mb-0.5"
                    style={{ color: sector.accentColor }}
                  >
                    {stat.value}
                  </div>
                  <div
                    className="font-body text-[11px] leading-tight"
                    style={{ color: subColor }}
                  >
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>

            {/* SEO keywords as tags */}
            <div className="flex flex-wrap gap-2">
              {sector.seoKeywords.slice(0, 4).map((kw) => (
                <span
                  key={kw}
                  className="font-body text-[11px] px-2.5 py-1 rounded-full"
                  style={{
                    backgroundColor: isLight ? 'rgba(10,22,40,0.05)' : 'rgba(255,255,255,0.06)',
                    color: isLight ? 'rgba(10,22,40,0.50)' : 'rgba(255,255,255,0.40)',
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  {kw}
                </span>
              ))}
            </div>
          </div>

          {/* Right: image */}
          <div className={`w-full lg:w-[480px] flex-shrink-0 ${reverse ? 'lg:order-1' : ''}`}>
            <div
              className="relative rounded-2xl overflow-hidden"
              style={{
                border: `1px solid ${sector.accentColor}25`,
                boxShadow: `0 20px 60px ${sector.accentColor}18`,
              }}
            >
              <img
                src={sector.heroImg}
                alt={`Software de facturación para ${sector.title} en República Dominicana`}
                className="w-full h-64 sm:h-72 object-cover object-top"
              />
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(to top, rgba(6,12,28,0.5) 0%, transparent 60%)' }}
              />
              <div
                className="absolute bottom-4 left-4 right-4 px-4 py-3 rounded-xl"
                style={{ backgroundColor: 'rgba(6,12,28,0.82)', backdropFilter: 'blur(12px)' }}
              >
                <div className="flex items-center gap-2.5">
                  <div
                    className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ backgroundColor: sector.accentColor + '25', color: sector.accentColor }}
                  >
                    <i className={`${sector.icon} text-sm`} />
                  </div>
                  <div>
                    <p className="font-heading text-sm font-bold text-white leading-none mb-0.5">
                      InnoFlex para {sector.title}
                    </p>
                    <p className="font-body text-[11px] text-white/50">
                      {sector.seoKeywords[0]}
                    </p>
                  </div>
                  <div
                    className="ml-auto w-6 h-6 flex items-center justify-center rounded-full"
                    style={{ backgroundColor: '#10b98130' }}
                  >
                    <i className="ri-check-line text-xs text-emerald-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12">
          {sector.features.map((feat) => (
            <div
              key={feat.title}
              className="p-5 rounded-xl"
              style={{
                background: typeof cardBg === 'string' && cardBg.startsWith('rgba')
                  ? cardBg
                  : undefined,
                backgroundColor: typeof cardBg === 'string' && !cardBg.startsWith('rgba')
                  ? cardBg
                  : undefined,
                border: `1px solid ${borderColor}`,
              }}
            >
              <div
                className="w-9 h-9 flex items-center justify-center rounded-xl mb-3"
                style={{ backgroundColor: sector.accentColor + '18', color: sector.accentColor }}
              >
                <i className={`${feat.icon} text-base`} />
              </div>
              <h3
                className="font-heading text-sm font-bold mb-1.5 leading-snug"
                style={{ color: textColor }}
              >
                {feat.title}
              </h3>
              <p
                className="font-body text-xs leading-relaxed"
                style={{ color: subColor }}
              >
                {feat.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Testimonial */}
        {sector.testimonial && (
          <div
            className="relative rounded-2xl p-6 sm:p-8 mb-10"
            style={{
              backgroundColor: sector.accentColor + '0a',
              border: `1px solid ${sector.accentColor}25`,
            }}
          >
            <div className="flex items-start gap-4">
              <div
                className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 mt-0.5"
                style={{ backgroundColor: sector.accentColor + '20', color: sector.accentColor }}
              >
                <i className="ri-double-quotes-l text-lg" />
              </div>
              <div>
                <p
                  className="font-body text-sm sm:text-base leading-relaxed mb-3 italic"
                  style={{ color: isLight ? 'rgba(10,22,40,0.72)' : 'rgba(255,255,255,0.72)' }}
                >
                  &ldquo;{sector.testimonial.quote}&rdquo;
                </p>
                <div className="flex items-center gap-2">
                  <span className="font-heading text-sm font-bold" style={{ color: textColor }}>
                    {sector.testimonial.name}
                  </span>
                  <span style={{ color: subColor }} className="font-body text-xs">
                    — {sector.testimonial.role}, {sector.testimonial.company}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* FAQ */}
        {sector.faq.length > 0 && (
          <div className="mb-6">
            <h3
              className="font-heading text-lg font-bold mb-4"
              style={{ color: textColor }}
            >
              Preguntas frecuentes — {sector.title}
            </h3>
            <div className="space-y-2">
              {sector.faq.map((item, idx) => (
                <div
                  key={idx}
                  className="rounded-xl overflow-hidden"
                  style={{ border: `1px solid ${borderColor}` }}
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between gap-4 px-5 py-4 cursor-pointer text-left transition-colors duration-150"
                    style={{
                      backgroundColor: openFaq === idx
                        ? sector.accentColor + '0d'
                        : isLight ? '#ffffff' : 'rgba(6,12,28,0.95)',
                    }}
                  >
                    <span
                      className="font-body text-sm font-semibold"
                      style={{ color: openFaq === idx ? sector.accentColor : textColor }}
                    >
                      {item.q}
                    </span>
                    <div
                      className="w-6 h-6 flex items-center justify-center flex-shrink-0"
                      style={{ color: sector.accentColor }}
                    >
                      <i
                        className="ri-arrow-down-s-line text-base transition-transform duration-200"
                        style={{ transform: openFaq === idx ? 'rotate(180deg)' : 'rotate(0deg)' }}
                      />
                    </div>
                  </button>
                  {openFaq === idx && (
                    <div
                      className="px-5 pb-4 pt-1"
                      style={{
                        backgroundColor: sector.accentColor + '07',
                        borderTop: `1px solid ${sector.accentColor}15`,
                      }}
                    >
                      <p
                        className="font-body text-sm leading-relaxed"
                        style={{ color: subColor }}
                      >
                        {item.a}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
